import React from 'react';
import './LayerPanel.css';

function LayerPanel({ elements, selectedElement, onSelect, onMoveForward, onMoveBackward, onBringToFront, onSendToBack }) {
  // Sort elements by z-index (highest on top)
  const sortedElements = [...elements].sort((a, b) => (b.zIndex || 0) - (a.zIndex || 0));
  
  // Get element type name for display
  const getElementTypeName = (type) => {
    const typeNames = {
      'text': 'Text',
      'image': 'Image',
      'video': 'Video',
      'shape': 'Shape',
      'rsvp-button': 'RSVP Button',
      'rsvp-form': 'RSVP Form',
      'background': 'Background'
    };
    
    return typeNames[type] || type;
  };
  
  // Get element preview content
  const getElementPreview = (element) => {
    const { type, content, style = {} } = element;
    
    switch(type) {
      case 'text':
        // For text elements, return the actual content (truncated)
        return content ? 
          (content.length > 20 ? content.substring(0, 20) + '...' : content) 
          : 'Text element';
      case 'image':
        // For image elements, return a thumbnail if available
        return content ? 
          <img 
            src={content} 
            alt="Preview" 
            style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
          /> : 'Image';
      case 'video':
        return 'Video';
      case 'shape':
        // For shapes, display a colored preview based on the shape's fill color
        const color = style.fill || style.backgroundColor || '#3b82f6';
        const shapeType = element.shapeType || style.shapeType || 'rectangle';
        
        return (
          <div className={`shape-preview shape-${shapeType}`} style={{ backgroundColor: color }}></div>
        );
      case 'rsvp-button':
        // Display a mini preview of the button with its actual styling
        return (
          <div 
            className="button-preview" 
            style={{ 
              backgroundColor: style.backgroundColor || '#4caf50',
              color: style.textColor || '#ffffff',
              borderRadius: style.borderRadius || '4px',
              padding: '2px 4px',
              fontSize: '10px',
              textAlign: 'center'
            }}
          >
            {style.text || 'RSVP'}
          </div>
        );
      case 'rsvp-form':
        return 'RSVP Form';
      case 'background':
        // Display a color swatch for background
        const bgColor = style.backgroundColor || '#ffffff';
        return (
          <div 
            className="background-preview" 
            style={{ 
              backgroundColor: bgColor,
              backgroundImage: style.backgroundImage || 'none'
            }}
          ></div>
        );
      default:
        return 'Element';
    }
  };
  
  // Get filtered elements - we now just return all elements
  const getFilteredElements = () => {
    return sortedElements;
  };
  
  const filteredElements = getFilteredElements();
  
  return (
    <div className="layer-panel">
      <div className="layer-panel-header">
        <div className="layer-panel-title">Layers</div>
        <button 
          className="close-layer-panel"
          onClick={() => window.dispatchEvent(new CustomEvent('toggle-layer-panel'))}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>
      </div>
      <div className="layer-list">
        {filteredElements.length === 0 ? (
          <div className="no-layers-message">
            No elements added yet
          </div>
        ) : (
          filteredElements.map(element => (
            <div 
              key={element.id}
              className={`layer-item ${element.id === selectedElement ? 'selected' : ''}`}
              onClick={() => onSelect(element.id)}
            >
              <div className="drag-handle">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="6" cy="6" r="1" />
                  <circle cx="6" cy="12" r="1" />
                  <circle cx="6" cy="18" r="1" />
                  <circle cx="12" cy="6" r="1" />
                  <circle cx="12" cy="12" r="1" />
                  <circle cx="12" cy="18" r="1" />
                  <circle cx="18" cy="6" r="1" />
                  <circle cx="18" cy="12" r="1" />
                  <circle cx="18" cy="18" r="1" />
                </svg>
              </div>
              
              <div className="layer-preview-wrapper">
                {typeof getElementPreview(element) === 'string' ? (
                  <div className="layer-preview-text">{getElementPreview(element)}</div>
                ) : (
                  <div className="layer-preview-visual">{getElementPreview(element)}</div>
                )}
              </div>
              
              <div className="layer-actions">
                <button 
                  className="layer-btn" 
                  title="Bring to Front"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBringToFront(element.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 20h20M2 4h20M12 16l-4-4m4 4l4-4m-4 4V8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                <button 
                  className="layer-btn" 
                  title="Move Forward"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveForward(element.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 19l-4-4m4 4l4-4m-4 4v-14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                <button 
                  className="layer-btn" 
                  title="Move Backward"
                  onClick={(e) => {
                    e.stopPropagation();
                    onMoveBackward(element.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5l-4 4m4-4l4 4m-4-4v14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
                
                <button 
                  className="layer-btn" 
                  title="Send to Back"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSendToBack(element.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M2 4h20M2 20h20M12 8l-4 4m4-4l4 4m-4-4v8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default LayerPanel; 